import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Home from './Home';
import Privacy from './Privacy';
import Terms from './Terms';
import DownloadPage from './DownloadPage';
import CitiesPage from './CitiesPage';
import Cookies from './Cookies';
import AcceptableUse from './AcceptableUse';
import Disclaimer from './Disclaimer';
import Business from './Business';
import 'flowbite';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import TiktokPixel from 'tiktok-pixel';
import ShareApp from './ShareApp';
import Affiliates from './Affiliates';
import { PROFESSIONS } from './data/professions';
import { CITY_PAGES } from './data/cities';

const TRACKING_ID = 'G-E24KQPS9NB'; //"UA-165871209-1";
ReactGA.initialize(TRACKING_ID);

const FB_PIXEL_ID = '1211509429775635';
const options = {
  autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
  debug: false, // enable logs
};
ReactPixel.init(FB_PIXEL_ID, null, options);

const TIKTOK_PIXEL_ID = 'CKQQ6ERC77U3M477TOA0';
const tiktokOptions = {
  debug: false, // enable logs
};
TiktokPixel.init(TIKTOK_PIXEL_ID, tiktokOptions);

const SITE_URL = 'https://www.lowkeybuzzer.com';

const App = () => {
  const isShareSubdomain = window.location.hostname.startsWith('share.');

  useEffect(() => {
    ReactGA.pageview(window.location.hostname + window.location.pathname + window.location.search);
    ReactPixel.pageView();
    TiktokPixel.pageView();
  }, []);

  if (isShareSubdomain) {
    return (
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Share Buzzer Access Instructions</title>
                  <meta
                    name="description"
                    content="Share buzzer access instructions to securely buzz in your guests."
                  />
                </Helmet>
                <ShareApp />
              </>
            }
          />
        </Routes>
      </Router>
    );
  } else {
    return (
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Helmet>
                  <title>Smart Buzzer App for Apartments Or Condos | Lowkey Buzzer</title>
                  <link rel="canonical" href={`${SITE_URL}/`} />
                  <meta
                    name="description"
                    content="Control your apartment or condo buzzer from your phone! No installation required, works with any buzzer, intercom, or call box that dials a phone number."
                  />
                </Helmet>
                <Home />
              </>
            }
          />

          {/* Business use case routes */}
          <Route
            path="/doordash"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz in DoorDash Deliveries</title>
                  <link rel="canonical" href={`${SITE_URL}/doordash`} />
                  <meta
                    name="description"
                    content="Lowkey ensures your DoorDash order arrives hot. Automatic buzzer entry means no more cold food or missed deliveries."
                  />
                </Helmet>
                <Home channel="doordash" />
              </>
            }
          />
          <Route
            path="/instacart"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz in Instacart Deliveries</title>
                  <link rel="canonical" href={`${SITE_URL}/instacart`} />
                  <meta
                    name="description"
                    content="Let Lowkey automatically buzz in your Instacart shopper. No more rushing to the phone or missing groceries."
                  />
                </Helmet>
                <Home channel="instacart" />
              </>
            }
          />
          <Route
            path="/amazon"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz In Your Amazon Packages</title>
                  <link rel="canonical" href={`${SITE_URL}/amazon`} />
                  <meta
                    name="description"
                    content="Automatically buzz in Amazon and courier drivers so your packages reach your door — no more missed deliveries or package theft."
                  />
                </Helmet>
                <Home channel="amazon_packages" />
              </>
            }
          />
          <Route
            path="/groceries"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz In Your Grocery Deliveries</title>
                  <link rel="canonical" href={`${SITE_URL}/groceries`} />
                  <meta
                    name="description"
                    content="Let grocery delivery drivers into your building automatically so your order arrives at your door — fresh and on time."
                  />
                </Helmet>
                <Home channel="groceries" />
              </>
            }
          />
          <Route
            path="/roommates"
            element={
              <>
                <Helmet>
                  <title>Share Apartment Buzzer Access With Roommates | Lowkey Buzzer</title>
                  <link rel="canonical" href={`${SITE_URL}/roommates`} />
                  <meta
                    name="description"
                    content="Share buzzer access with every roommate so anyone can buzz in guests and deliveries, no matter whose phone the buzzer calls."
                  />
                </Helmet>
                <Home channel="roommates" />
              </>
            }
          />
          <Route
            path="/intercom"
            element={
              <>
                <Helmet>
                  <title>Apartment Intercom App | Lowkey</title>
                  <link rel="canonical" href={`${SITE_URL}/intercom`} />
                  <meta
                    name="description"
                    content="Turn your apartment intercom into an app on every phone. Answer the door, auto-buzz deliveries, and share keypad passcodes. No hardware, works with any intercom that dials a phone."
                  />
                </Helmet>
                <Home channel="intercom" />
              </>
            }
          />
          <Route
            path="/multiple-phones"
            element={
              <>
                <Helmet>
                  <title>Forward Your Apartment Buzzer to Multiple Phones | Lowkey Buzzer</title>
                  <link rel="canonical" href={`${SITE_URL}/multiple-phones`} />
                  <meta
                    name="description"
                    content="Ring more than one phone when your buzzer goes off. Lowkey forwards your apartment or condo buzzer to everyone in your household, so anyone can buzz guests and deliveries in."
                  />
                </Helmet>
                <Home channel="multiple_phones" />
              </>
            }
          />
          <Route
            path="/smb"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz In Clients For Your Business</title>
                  <link rel="canonical" href={`${SITE_URL}/smb`} />
                  <meta
                    name="description"
                    content="Stop wasting time buzzing in clients. Lowkey automates buzzer entry for your business, saving you time and hassle."
                  />
                </Helmet>
                <Home channel="buzz_in_clients" />
              </>
            }
          />
          <Route
            path="/property-access-software"
            element={
              <>
                <Helmet>
                  <title>Property Access Software for Apartment or Condo Buildings | Lowkey Buzzer</title>
                  <link rel="canonical" href={`${SITE_URL}/property-access-software`} />
                  <meta
                    name="description"
                    content="Property access software that lets tenants buzz in guests and deliveries from their phone. No rewiring, works across every building you manage, run from one app."
                  />
                </Helmet>
                <Home channel="property_access_software" />
              </>
            }
          />
          <Route
            path="/caregivers"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Simplify Access for Your Elderly Parent's Caregivers</title>
                  <link rel="canonical" href={`${SITE_URL}/caregivers`} />
                  <meta
                    name="description"
                    content="Provide secure, convenient access for healthcare workers and assistants caring for your elderly parents in their condo or apartment."
                  />
                </Helmet>
                <Home channel="caregivers" />
              </>
            }
          />
          <Route
            path="/assisted-living"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Smart Buzzer Access for Assisted Living</title>
                  <link rel="canonical" href={`${SITE_URL}/assisted-living`} />
                  <meta
                    name="description"
                    content="Give caregivers, medical staff, and family secure, automatic buzzer access to your loved one's assisted living residence. No installation required."
                  />
                </Helmet>
                <Home channel="assisted_living" />
              </>
            }
          />
          <Route
            path="/delivery"
            element={
              <>
                <Helmet>
                  <title>Automatically Buzz In Package Deliveries For Apartments & Condos | Lowkey Buzzer</title>
                  <link rel="canonical" href={`${SITE_URL}/delivery`} />
                  <meta
                    name="description"
                    content="Automatically buzz in your deliveries without answering your phone"
                  />
                </Helmet>
                <Home channel="delivery" />
              </>
            }
          />
          <Route
            path="/rover"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz In Your Rover Dog Walker</title>
                  <link rel="canonical" href={`${SITE_URL}/rover`} />
                  <meta
                    name="description"
                    content="Let your Rover dog walker or sitter into your building automatically — no rushing to the buzzer. Your pup gets their walk on time."
                  />
                </Helmet>
                <Home channel="rover" />
              </>
            }
          />
          <Route
            path="/pet-sitting"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz In Your Pet Sitter</title>
                  <link rel="canonical" href={`${SITE_URL}/pet-sitting`} />
                  <meta
                    name="description"
                    content="Give your pet sitter secure, automatic access to your building while you're away. No lockboxes, no missed visits."
                  />
                </Helmet>
                <Home channel="pet_sitting" />
              </>
            }
          />
          <Route
            path="/airbnb"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automate Buzzer Access & Self Check-in For Airbnb Guests</title>
                  <link rel="canonical" href={`${SITE_URL}/airbnb`} />
                  <meta
                    name="description"
                    content="Give Airbnb guests and your cleaning crew secure, automatic buzzer access for the dates of their stay — no key handoffs or meeting in person."
                  />
                </Helmet>
                <Home channel="airbnb" />
              </>
            }
          />
          <Route
            path="/short-term-rentals"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automate Buzzer Access & Self Check-in For Short Term Rentals</title>
                  <link rel="canonical" href={`${SITE_URL}/short-term-rentals`} />
                  <meta
                    name="description"
                    content="Give short-term rental guests and cleaners automatic, time-limited buzzer access. Manage entry across all your units from one app."
                  />
                </Helmet>
                <Home channel="short_term_rentals" />
              </>
            }
          />
          <Route
            path="/ubereats"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Automatically Buzz in UberEats Deliveries</title>
                  <link rel="canonical" href={`${SITE_URL}/ubereats`} />
                  <meta
                    name="description"
                    content="Lowkey ensures your UberEats order arrives hot. Automatic buzzer entry means no more cold food or missed deliveries."
                  />
                </Helmet>
                <Home channel="ubereats" />
              </>
            }
          />
          <Route
            path="/business"
            element={
              <>
                <Helmet>
                  <title>Lowkey for Businesses - Smart Buzzer For Apartments Or Condos</title>
                  <link rel="canonical" href={`${SITE_URL}/business`} />
                  <meta
                    name="description"
                    content="Discover how Lowkey can transform your business operations with smart, secure access control."
                  />
                </Helmet>
                <Business />
              </>
            }
          />
          <Route
            path="/affiliates"
            element={
              <>
                <Helmet>
                  <title>Lowkey Affiliate Program - Partner With Us</title>
                  <link rel="canonical" href={`${SITE_URL}/affiliates`} />
                  <meta
                    name="description"
                    content="Join Lowkey's affiliate program and earn by promoting our smart buzzer solutions."
                  />
                </Helmet>
                <Affiliates />
              </>
            }
          />

          {/* Profession / client-services landing pages (config-driven) */}
          {Object.entries(PROFESSIONS).map(([channel, p]) => (
            <Route
              key={channel}
              path={p.path}
              element={
                <>
                  <Helmet>
                    <title>{p.seoTitle}</title>
                    <link rel="canonical" href={`${SITE_URL}${p.path}`} />
                    <meta name="description" content={p.metaDescription} />
                  </Helmet>
                  <Home channel={channel} />
                </>
              }
            />
          ))}

          {/* City landing pages (config-driven) */}
          {Object.entries(CITY_PAGES).map(([channel, c]) => (
            <Route
              key={channel}
              path={c.path}
              element={
                <>
                  <Helmet>
                    <title>{c.seoTitle}</title>
                    <link rel="canonical" href={`${SITE_URL}${c.path}`} />
                    <meta name="description" content={c.metaDescription} />
                  </Helmet>
                  <Home channel={channel} />
                </>
              }
            />
          ))}

          {/* Other routes */}
          <Route
            path="/privacy"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Privacy Policy</title>
                  <link rel="canonical" href={`${SITE_URL}/privacy`} />
                  <meta name="description" content="Read our privacy policy to understand how we protect your data." />
                </Helmet>
                <Privacy />
              </>
            }
          />
          <Route
            path="/terms"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Terms of Service</title>
                  <link rel="canonical" href={`${SITE_URL}/terms`} />
                  <meta
                    name="description"
                    content="Review our terms of service for using Lowkey's smart buzzer system."
                  />
                </Helmet>
                <Terms />
              </>
            }
          />
          <Route
            path="/cookies"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Cookie Policy</title>
                  <link rel="canonical" href={`${SITE_URL}/cookies`} />
                  <meta name="description" content="Learn about how we use cookies on our website." />
                </Helmet>
                <Cookies />
              </>
            }
          />
          <Route
            path="/acceptable-use"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Acceptable Use Policy</title>
                  <link rel="canonical" href={`${SITE_URL}/acceptable-use`} />
                  <meta
                    name="description"
                    content="Understand our guidelines for acceptable use of Lowkey's services."
                  />
                </Helmet>
                <AcceptableUse />
              </>
            }
          />
          <Route
            path="/disclaimer"
            element={
              <>
                <Helmet>
                  <title>Lowkey - Disclaimer</title>
                  <link rel="canonical" href={`${SITE_URL}/disclaimer`} />
                  <meta
                    name="description"
                    content="Read important disclaimers regarding the use of Lowkey's smart buzzer system."
                  />
                </Helmet>
                <Disclaimer />
              </>
            }
          />
          <Route
            path="/cities"
            element={
              <>
                <Helmet>
                  <title>Apartment Buzzer App Coverage | Lowkey by City</title>
                  <link rel="canonical" href={`${SITE_URL}/cities`} />
                  <meta
                    name="description"
                    content="Find your city and see how Lowkey works with the apartment and condo buzzers in your area. Lowkey covers cities across the US and Canada."
                  />
                </Helmet>
                <CitiesPage />
              </>
            }
          />
          <Route
            path="/download"
            element={
              <>
                <Helmet>
                  <title>Download Lowkey Buzzer | Apartment Buzzer App for iOS & Android</title>
                  <link rel="canonical" href={`${SITE_URL}/download`} />
                  <meta
                    name="description"
                    content="Get Lowkey on the App Store or Google Play and control your apartment or condo buzzer from your phone in minutes. Start your 14 day trial."
                  />
                </Helmet>
                <DownloadPage />
              </>
            }
          />
        </Routes>
      </Router>
    );
  }
};

export default App;
